import type {MaintenanceItem,MaintenanceItemInput} from "./api";
import {createClientId} from "./lib/create-client-id";

export type MaintenanceItemDraft = Omit<MaintenanceItem, "id"|"affected_quantity"> & {
  id?: string;
  clientId: string;
  affected_quantity:number|"";
  observations:string;
};

export type MaintenanceItemErrors = Partial<Record<"description"|"maintenance_type_id"|"affected_quantity"|"inputs",string>>;

export function createEmptyMaintenanceItem(): MaintenanceItemDraft {
  return {
    clientId:createClientId("maintenance-item"),
    product_id:"",
    table_code:"",
    focco_code:"",
    description:"",
    line:"",
    family:"",
    model:"",
    configuration:"",
    measures:"",
    observations:"",
    affected_quantity:1
  };
}

export function toMaintenanceItemDraft(item:MaintenanceItem):MaintenanceItemDraft {
  return {...item,observations:item.observations??"",clientId:item.id||createClientId("maintenance-item")};
}

export function updateMaintenanceItem(items:MaintenanceItemDraft[],clientId:string,patch:Partial<MaintenanceItemDraft>){
  return items.map(item=>item.clientId===clientId?{...item,...patch}:item);
}

export function removeMaintenanceItem(items:MaintenanceItemDraft[],clientId:string){
  return items.filter(item=>item.clientId!==clientId);
}

export function groupMaintenanceItemInputs(inputs:MaintenanceItemInput[]=[]){
  const grouped=new Map<number,MaintenanceItemInput[]>();
  for(const input of inputs){
    const unitNumber=Math.max(1,Number(input.unit_number)||1);
    grouped.set(unitNumber,[...(grouped.get(unitNumber)||[]),input]);
  }
  return [...grouped.entries()].sort(([left],[right])=>left-right).map(([unitNumber,unitInputs])=>({unitNumber,inputs:unitInputs}));
}

export function updateMaintenanceItemInput(inputs:MaintenanceItemInput[]|undefined,inputId:string,patch:Partial<MaintenanceItemInput>){
  return (inputs||[]).map(input=>input.id===inputId?{...input,...patch}:input);
}

export function createMaintenanceItemInput(item:Pick<MaintenanceItemDraft,"id">,unitNumber:number,value:{code:string;description:string;quantity:number;unit?:string}):MaintenanceItemInput{
  return {id:createClientId("maintenance-input"),maintenance_id:"",maintenance_item_id:item.id||"",unit_number:unitNumber,input_code:value.code.trim(),description:value.description.trim(),quantity:value.quantity,unit:value.unit?.trim()||undefined};
}

export function removeMaintenanceItemInput(inputs:MaintenanceItemInput[]|undefined,inputId:string){
  return (inputs||[]).filter(input=>input.id!==inputId);
}

export function isMaintenanceItemEmpty(item:MaintenanceItemDraft){
  return !item.product_id.trim()&&!(item.table_code||"").trim()&&!(item.focco_code||"").trim()&&!item.description.trim()&&!(item.line||"").trim()&&!(item.family||"").trim()&&
    !(item.model||"").trim()&&!(item.configuration||"").trim()&&!(item.measures||"").trim()&&!item.observations.trim()&&item.affected_quantity===1;
}

export function validateMaintenanceItem(item:MaintenanceItemDraft):MaintenanceItemErrors {
  const errors:MaintenanceItemErrors={};
  if(!item.description.trim())errors.description="INFORME O PRODUTO.";
  if(!item.maintenance_type_id)errors.maintenance_type_id="SELECIONE O TIPO DE MANUTENÇÃO.";
  if(item.affected_quantity===""||!Number.isInteger(item.affected_quantity)||item.affected_quantity<=0)errors.affected_quantity="A QUANTIDADE AFETADA DEVE SER MAIOR QUE ZERO.";
  if((item.inputs||[]).some(input=>!input.input_code.trim()||!input.description.trim()||!Number.isFinite(input.quantity)||input.quantity<0||input.unit_number<1||input.unit_number>Number(item.affected_quantity)))errors.inputs="PREENCHA CÓDIGO, DESCRIÇÃO, QUANTIDADE E UNIDADE AFETADA VÁLIDA EM TODOS OS INSUMOS.";
  return errors;
}

export function formatMaintenanceItemTitle(item:Pick<MaintenanceItemDraft,"product_id"|"description">,position:number){
  const prefix=`ITEM ${position}`;
  const product=item.description?.trim();
  return product?prefix+": "+product:prefix+": PRODUTO NÃO SELECIONADO";
}

export function formatMaintenanceItemCount(count:number){
  if(count===0)return"NENHUM ITEM CADASTRADO";
  return count===1?"1 ITEM CADASTRADO":`${count} ITENS CADASTRADOS`;
}
